import React, { FC, useState, useRef } from 'react';
import { Box, Typography, TextField, Button, Alert, CircularProgress } from '@mui/material';
import { WalletStyles } from '../styles';
import { listUnspent } from '../../../webservice/mProviders';
import { homenetwork, homepvtKey, compState } from '../../../Headerr';
import { bsv } from '../../../scrypt-ts-this/distthis/index';
import { broadcast } from '../../../webservice/mProviders';

const WalletSend: FC = () => {
  const [recipientAddress, setRecipientAddress] = useState('');
  const [amount, setAmount] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [showScanner, setShowScanner] = useState(false);
  const scannerRef = useRef<any>(null);

  const handleScan = async () => {
    if (showScanner) {
      if (scannerRef.current) {
        try { await scannerRef.current.stop(); } catch (e) { /* ignore */ }
        scannerRef.current = null;
      }
      setShowScanner(false);
      return;
    }

    setShowScanner(true);

    // Dynamically import html5-qrcode
    try {
      const { Html5Qrcode } = await import('html5-qrcode');
      setTimeout(async () => {
        const scanner = new Html5Qrcode('qr-reader');
        scannerRef.current = scanner;
        try {
          await scanner.start(
            { facingMode: 'environment' },
            { fps: 10, qrbox: { width: 250, height: 250 } },
            (decodedText: string) => {
              // Parse bitcoin URI or plain address
              let addr = decodedText;
              let amt = '';
              if (decodedText.startsWith('bitcoin:')) {
                const parts = decodedText.replace('bitcoin:', '').split('?');
                addr = parts[0];
                if (parts[1]) {
                  const params = new URLSearchParams(parts[1]);
                  amt = params.get('amount') || '';
                }
              }
              setRecipientAddress(addr);
              if (amt) setAmount(amt);
              scanner.stop().then(() => {
                scannerRef.current = null;
                setShowScanner(false);
              });
            },
            () => { /* ignore errors during scanning */ }
          );
        } catch (err) {
          console.error('Camera error:', err);
          setResult({ type: 'error', message: 'Não foi possível acessar a câmera. Insira o endereço manualmente.' });
          setShowScanner(false);
        }
      }, 100);
    } catch (err) {
      console.error('Failed to load QR scanner:', err);
      setResult({ type: 'error', message: 'Erro ao carregar scanner QR.' });
      setShowScanner(false);
    }
  };

  const handleSend = async () => {
    setResult(null);
    if (!recipientAddress || !amount) {
      setResult({ type: 'error', message: 'Preencha o endereço e o valor.' });
      return;
    }

    const satoshis = Math.round(parseFloat(amount) * 100000000);
    if (isNaN(satoshis) || satoshis <= 0) {
      setResult({ type: 'error', message: 'Valor inválido.' });
      return;
    }

    if (homepvtKey.length !== 64) {
      setResult({ type: 'error', message: 'Insira sua chave privada nas credenciais primeiro.' });
      return;
    }

    setSending(true);
    try {
      const privateKey = bsv.PrivateKey.fromHex(homepvtKey, homenetwork);
      privateKey.compAdd(compState);
      const fromAddress = bsv.Address.fromPrivateKey(privateKey).toString();
      const UTXOs = await listUnspent(fromAddress, homenetwork);

      if (!UTXOs || UTXOs.length === 0) {
        setResult({ type: 'error', message: 'Nenhum UTXO disponível.' });
        setSending(false);
        return;
      }

      const tx = new bsv.Transaction();
      let totalInput = 0;
      for (const utxo of UTXOs) {
        tx.from(utxo);
        totalInput += utxo.satoshis;
        if (totalInput >= satoshis + 500) break; // enough with fee margin
      }

      if (totalInput < satoshis + 200) {
        setResult({ type: 'error', message: 'Saldo insuficiente.' });
        setSending(false);
        return;
      }

      tx.to(recipientAddress, satoshis);
      tx.change(fromAddress);
      tx.feePerKb(500);
      tx.sign(privateKey);

      tx.serialize(); // validate serialization
      const txid = await broadcast(tx, homenetwork);

      if (txid) {
        setResult({ type: 'success', message: `Transação enviada! TXID: ${txid}` });
        setRecipientAddress('');
        setAmount('');
      } else {
        setResult({ type: 'error', message: 'Falha ao transmitir a transação.' });
      }
    } catch (e: any) {
      console.error('Send error:', e);
      setResult({ type: 'error', message: `Erro: ${e.message || 'Falha no envio'}` });
    } finally {
      setSending(false);
    }
  };

  return (
    <Box>
      <Box sx={WalletStyles.card}>
        <Typography sx={WalletStyles.sectionTitle}>Enviar BSV</Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Endereço Destino"
            value={recipientAddress}
            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setRecipientAddress(e.target.value)}
            fullWidth
            variant="outlined"
            sx={WalletStyles.inputField}
          />

          <Button
            variant="outlined"
            onClick={handleScan}
            sx={WalletStyles.outlinedButton}
          >
            {showScanner ? '❌ Fechar Scanner' : '📷 Escanear QR Code'}
          </Button>

          {showScanner && (
            <Box id="qr-reader" sx={{ width: '100%', maxWidth: '400px', margin: '0 auto' }} />
          )}

          <TextField
            label="Valor (BSV)"
            value={amount}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAmount(e.target.value)}
            fullWidth
            variant="outlined"
            type="number"
            inputProps={{ step: '0.00000001', min: '0' }}
            sx={WalletStyles.inputField}
          />

          <Button
            variant="contained"
            onClick={handleSend}
            disabled={sending}
            sx={WalletStyles.actionButton}
          >
            {sending ? <CircularProgress size={24} sx={{ color: 'white' }} /> : '📤 Enviar'}
          </Button>

          {result && (
            <Alert severity={result.type} sx={{ mt: 1, wordBreak: 'break-all' }}>
              {result.message}
            </Alert>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default WalletSend;
